import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ArrowRight, FileText, Map, Calculator, ShieldCheck, Sparkles, Copy, Check, Stethoscope, Sun, BadgeCheck } from "lucide-react";
import { apiClient } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";

const DOCTOR_HERO = `${process.env.REACT_APP_BACKEND_URL}/api/asset/doctor-hero.png`;
const DOCTOR_THUMBSUP = `${process.env.REACT_APP_BACKEND_URL}/api/asset/doctor-thumbsup.png`;

export default function LandingLegacy() {
  const { t, i18n } = useTranslation();
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState("madrid");
  const [bill, setBill] = useState(120);
  const [preview, setPreview] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [reportPrice, setReportPrice] = useState(89);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [magicLink, setMagicLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [referrerCode, setReferrerCode] = useState("");

  useEffect(() => {
    apiClient.get("/regions").then((r) => setRegions(r.data.regions));
    apiClient.get("/calc/report-price").then((r) => setReportPrice(r.data.report_price_eur)).catch(() => {});
    // Capture referral code from URL (?ref=XXXX) and persist in localStorage.
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      if (ref) {
        const cleaned = ref.trim().toUpperCase().slice(0, 16);
        if (cleaned) {
          localStorage.setItem("doctorsol_ref", cleaned);
          setReferrerCode(cleaned);
        }
      } else {
        const stored = localStorage.getItem("doctorsol_ref");
        if (stored) setReferrerCode(stored);
      }
    } catch { /* localStorage may be unavailable */ }
  }, []);

  useEffect(() => {
    if (!region || !bill || bill <= 0) return;
    setLoadingPrev(true);
    apiClient.post("/calc/preview", { region, monthly_bill_eur: Number(bill) })
      .then((r) => setPreview(r.data))
      .finally(() => setLoadingPrev(false));
  }, [region, bill]);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!name || !email || !whatsapp) { toast.error(t("landing.errors.complete_fields")); return; }
    setSubmitting(true);
    try {
      const r = await apiClient.post("/leads", {
        name, email, whatsapp, region, monthly_bill_eur: Number(bill),
        language: i18n.language?.split("-")[0] || "es",
        referrer_code: referrerCode || null,
      });
      const fullLink = `${window.location.origin}${r.data.magic_link}`;
      setMagicLink({ url: fullLink, token: r.data.magic_token });
      toast.success(t("landing.magic_link_ready"));
    } catch (err) {
      toast.error("Error: " + (err?.response?.data?.detail || err.message));
    } finally { setSubmitting(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(magicLink.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative min-h-screen bg-white">
      <Header />

      {/* Hero with mascot */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#FFF7ED] to-white">
        <div className="absolute top-20 right-10 md:right-32 w-72 h-72 rounded-full bg-[#FBBF24] opacity-20 blur-3xl" />
        <div className="absolute top-40 right-40 md:right-80 w-48 h-48 rounded-full bg-[#E78938] opacity-25 blur-2xl" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFF7ED] border border-[#E78938]/20 mb-6">
              <Stethoscope className="w-3.5 h-3.5 text-[#E78938]" strokeWidth={2.2} />
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#E78938] font-semibold">{t("landing.kicker")}</span>
            </div>
            <h1 className="serif text-4xl sm:text-5xl lg:text-6xl text-[#0F172A] leading-[1.05]">
              {t("landing.hero_title_1")}<br />
              <span className="text-[#E78938]">{t("landing.hero_title_2")}</span>
            </h1>
            <p className="mt-6 text-lg text-[#475569] max-w-xl leading-relaxed">
              {t("landing.hero_subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3 text-sm">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E2E8F0] text-[#475569]">
                <BadgeCheck className="w-4 h-4 text-[#E78938]" strokeWidth={2} /> {t("landing.badge_no_commission")}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E2E8F0] text-[#475569]">
                <Sparkles className="w-4 h-4 text-[#E78938]" strokeWidth={2} /> {t("landing.badge_ai")}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E2E8F0] text-[#475569]">
                <FileText className="w-4 h-4 text-[#E78938]" strokeWidth={2} /> {t("landing.badge_pages")}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E2E8F0] text-[#475569]">
                <ShieldCheck className="w-4 h-4 text-[#E78938]" strokeWidth={2} /> {t("landing.badge_minutes")}
              </span>
            </div>
            <div className="mt-10 flex items-baseline gap-4">
              <span className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">{t("landing.your_report")}</span>
              <span className="serif text-5xl text-[#0F172A] tnum">€{reportPrice}</span>
              <span className="text-sm text-[#64748B]">{t("landing.iva_included")}</span>
            </div>
          </div>
          <div className="md:col-span-5 relative flex justify-center">
            <div className="absolute inset-10 rounded-full bg-[#FBBF24] opacity-30 blur-3xl sun-glow" />
            <img src={DOCTOR_HERO} alt="Doctor Sol" className="relative w-full max-w-md sun-rise"
              data-testid="doctor-hero" />
          </div>
        </div>
      </section>

      {/* Calculator + Lead Form */}
      <section id="calc" className="relative max-w-6xl mx-auto px-6 md:px-10 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Calculator */}
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-7 md:p-9 shadow-[0_8px_30px_rgba(15,23,42,0.06)]">
            <div className="flex items-center gap-2 text-[#E78938] uppercase text-xs tracking-[0.2em] mb-3 font-semibold">
              <Calculator className="w-4 h-4" strokeWidth={2} /> {t("landing.calc_kicker")}
            </div>
            <h2 className="serif text-3xl text-[#0F172A] mb-6">{t("landing.calc_title")}</h2>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">{t("landing.region")}</label>
                <select data-testid="calc-region" value={region} onChange={(e) => setRegion(e.target.value)}
                  className="w-full bg-[#FFF7ED] rounded-xl px-4 py-3 text-[#0F172A] focus:ring-2 focus:ring-[#E78938]/40 focus:bg-white outline-none transition-all border border-transparent focus:border-[#E78938]/30">
                  {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">
                  {t("landing.monthly_bill")}: <span className="text-[#E78938] tnum font-semibold">€ {bill}</span>
                </label>
                <input data-testid="calc-bill" type="range" min="40" max="500" step="10"
                  value={bill} onChange={(e) => setBill(e.target.value)} className="w-full accent-[#E78938]" />
                <div className="flex justify-between text-xs text-[#94A3B8] mt-1"><span>€40</span><span>€500</span></div>
              </div>
            </div>

            <div className="mt-6 p-5 rounded-xl bg-gradient-to-br from-[#FFF7ED] to-[#FFFBF5] border border-[#FED7AA]" data-testid="savings-preview">
              {loadingPrev || !preview ? (
                <div className="text-sm text-[#94A3B8]">{t("landing.calculating")}</div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#E78938] font-semibold">{t("landing.savings_year")}</div>
                    <div className="serif text-3xl text-[#E78938] tnum mt-1">€ {preview.annual_savings_eur.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-semibold">{t("landing.payback")}</div>
                    <div className="serif text-3xl text-[#0F172A] tnum mt-1">{preview.payback_years} {t("landing.years")}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-semibold">{t("landing.system_suggested")}</div>
                    <div className="text-lg text-[#0F172A] tnum mt-0.5 font-semibold">{preview.system_kwp} kWp</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8] font-semibold">{t("landing.co2_saved")}</div>
                    <div className="text-lg text-[#0F172A] tnum mt-0.5 font-semibold">{preview.co2_saved_kg_year.toLocaleString()} {t("landing.kg_year")}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Lead form */}
          <div className="bg-[#0F172A] text-white rounded-2xl p-7 md:p-9 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-[#E78938] opacity-30 blur-3xl" />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.2em] text-[#FBBF24] mb-3 font-semibold">{t("landing.form_kicker")}</div>
              <h2 className="serif text-3xl mb-2">{t("landing.form_title")}</h2>
              <p className="text-sm text-white/70 mb-6">{t("landing.form_subtitle")}</p>

              {referrerCode && !magicLink && (
                <div data-testid="referral-banner" className="mb-5 rounded-xl border border-[#FBBF24]/40 bg-[#FBBF24]/10 px-4 py-3 flex items-center gap-3">
                  <Sparkles className="w-4 h-4 text-[#FBBF24] shrink-0" />
                  <div className="text-sm">
                    <div className="text-[#FBBF24] font-semibold">{t("landing.referral_active_title")}</div>
                    <div className="text-white/70 text-xs mt-0.5">{t("landing.referral_active_desc", { code: referrerCode })}</div>
                  </div>
                </div>
              )}

              {!magicLink ? (
                <form onSubmit={submitLead} className="space-y-4" data-testid="lead-form">
                  <div>
                    <label htmlFor="lead-name" className="block text-xs uppercase tracking-wider text-white/70 mb-1.5 font-semibold">{t("landing.name")}</label>
                    <input id="lead-name" data-testid="input-name" placeholder={t("landing.name_placeholder")} value={name} onChange={(e) => setName(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/50 focus:border-[#E78938] focus:bg-white/15 outline-none transition" />
                  </div>
                  <div>
                    <label htmlFor="lead-email" className="block text-xs uppercase tracking-wider text-white/70 mb-1.5 font-semibold">{t("landing.email")}</label>
                    <input id="lead-email" data-testid="input-email" type="email" placeholder={t("landing.email_placeholder")} value={email} onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/50 focus:border-[#E78938] focus:bg-white/15 outline-none transition" />
                  </div>
                  <div>
                    <label htmlFor="lead-whatsapp" className="block text-xs uppercase tracking-wider text-white/70 mb-1.5 font-semibold">{t("landing.whatsapp")}</label>
                    <input id="lead-whatsapp" data-testid="input-whatsapp" placeholder={t("landing.whatsapp_placeholder")} value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/50 focus:border-[#E78938] focus:bg-white/15 outline-none transition" />
                  </div>
                  <button data-testid="submit-lead" disabled={submitting} type="submit"
                    className="w-full bg-[#E78938] hover:bg-[#C5701D] text-white rounded-xl px-6 py-3.5 font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                    {submitting ? t("landing.generating") : <>{t("landing.generate_dossier")} <ArrowRight className="w-4 h-4" /></>}
                  </button>
                  <p className="text-[11px] text-white/50">{t("landing.no_spam")}</p>
                </form>
              ) : (
                <div className="space-y-4" data-testid="magic-link-card">
                  <div className="text-sm text-white/80">{t("landing.magic_link_label")}</div>
                  <div className="bg-white/10 border border-white/20 rounded-xl p-4 break-all text-sm font-mono">{magicLink.url}</div>
                  <div className="flex gap-3">
                    <button onClick={copyLink} data-testid="copy-link"
                      className="flex-1 bg-white/10 hover:bg-white/20 rounded-xl px-4 py-3 transition flex items-center justify-center gap-2">
                      {copied ? <><Check className="w-4 h-4" />{t("landing.copied")}</> : <><Copy className="w-4 h-4" />{t("landing.copy")}</>}
                    </button>
                    <Link to={`/portal?token=${magicLink.token}`} data-testid="goto-portal"
                      className="flex-1 bg-[#E78938] hover:bg-[#C5701D] rounded-xl px-4 py-3 text-center transition flex items-center justify-center gap-2 font-semibold">
                      {t("landing.open_portal")} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Two scenarios */}
      <section className="bg-[#0F172A] text-white py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.2em] text-[#FBBF24] mb-4 font-semibold">{t("landing.scenarios_kicker")}</div>
          <h2 className="serif text-4xl md:text-5xl mb-12 whitespace-pre-line">{t("landing.scenarios_title")}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/5 border border-red-500/30 rounded-2xl p-7">
              <h3 className="serif text-2xl text-red-400 mb-4">{t("landing.without_report_title")}</h3>
              <p className="text-white/70 text-sm mb-5">{t("landing.without_report_text")}</p>
              <ul className="space-y-3 text-sm">
                <li className="border-l-2 border-red-500 pl-3"><b>Instalador 1:</b> 12 paneles · €9.800</li>
                <li className="border-l-2 border-red-500 pl-3"><b>Instalador 2:</b> 16 paneles + batería · €18.500</li>
                <li className="border-l-2 border-red-500 pl-3"><b>Instalador 3:</b> 10 paneles + aerotermia · €24.000</li>
              </ul>
              <div className="mt-5 pt-5 border-t border-white/10 text-sm text-white/70">
                {t("landing.without_report_result")} <b className="text-red-400">{t("landing.without_report_dont_know")}</b> {t("landing.without_report_lost")} <b>€3.000-€8.000</b>.
              </div>
            </div>
            <div className="bg-white/5 border border-[#E78938]/40 rounded-2xl p-7">
              <h3 className="serif text-2xl text-[#FBBF24] mb-4">{t("landing.with_report_title")}</h3>
              <p className="text-white/70 text-sm mb-5">{t("landing.with_report_text")}</p>
              <ul className="space-y-2.5 text-sm">
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#FBBF24] mt-0.5 shrink-0" />{t("landing.with_report_b1")}</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#FBBF24] mt-0.5 shrink-0" />{t("landing.with_report_b2")}</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#FBBF24] mt-0.5 shrink-0" />{t("landing.with_report_b3")}</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#FBBF24] mt-0.5 shrink-0" />{t("landing.with_report_b4")}</li>
                <li className="flex gap-2"><Check className="w-4 h-4 text-[#FBBF24] mt-0.5 shrink-0" />{t("landing.with_report_b5")}</li>
              </ul>
              <div className="mt-5 pt-5 border-t border-white/10 text-sm">
                <span className="text-white/70">{t("landing.with_report_invest")}{reportPrice}</span><br/>
                <span className="text-[#FBBF24]"><b>{t("landing.with_report_save")}</b></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="how" className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="text-xs uppercase tracking-[0.2em] text-[#E78938] font-semibold mb-4">{t("landing.process_kicker")}</div>
        <h2 className="serif text-4xl md:text-5xl text-[#0F172A] mb-14 max-w-3xl">{t("landing.process_title")}</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { i: "01", t: t("landing.step1_title"), d: t("landing.step1_desc"), icon: <Stethoscope className="w-5 h-5" strokeWidth={2} /> },
            { i: "02", t: t("landing.step2_title"), d: t("landing.step2_desc"), icon: <Sparkles className="w-5 h-5" strokeWidth={2} /> },
            { i: "03", t: t("landing.step3_title"), d: t("landing.step3_desc"), icon: <FileText className="w-5 h-5" strokeWidth={2} /> },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#E2E8F0] p-7 hover-lift">
              <div className="text-xs mono text-[#94A3B8] mb-3">{s.i}</div>
              <div className="w-10 h-10 rounded-xl bg-[#FFF7ED] flex items-center justify-center text-[#E78938] mb-4">{s.icon}</div>
              <div className="serif text-2xl text-[#0F172A] mb-2">{s.t}</div>
              <div className="text-sm text-[#64748B] leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Topics — what's inside the report */}
      <section className="bg-white py-20 border-t border-b border-[#F1F5F9]">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-[#E78938] font-semibold mb-4">{t("landing.topics_kicker")}</div>
            <h2 className="serif text-4xl text-[#0F172A] mb-3">{t("landing.topics_title")}</h2>
            <p className="text-[#64748B] max-w-2xl mx-auto">{t("landing.topics_subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { title: t("landing.topics.tech_title"), items: t("landing.topics.tech_items") },
              { title: t("landing.topics.sizing_title"), items: t("landing.topics.sizing_items") },
              { title: t("landing.topics.subsidies_title"), items: t("landing.topics.subsidies_items") },
              { title: t("landing.topics.roi_title"), items: t("landing.topics.roi_items") },
              { title: t("landing.topics.implementation_title"), items: t("landing.topics.implementation_items") },
              { title: t("landing.topics.legal_title"), items: t("landing.topics.legal_items") },
            ].map((b, i) => (
              <div key={i} data-testid={`topic-card-${i}`} className="bg-[#FFF7ED] border border-[#FED7AA] rounded-2xl p-6 hover-lift">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 shrink-0 rounded-lg bg-[#E78938] text-white flex items-center justify-center font-semibold">{i + 1}</div>
                  <div>
                    <div className="serif text-xl text-[#0F172A] mb-1">{b.title}</div>
                    <div className="text-sm text-[#475569] leading-relaxed">{b.items}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we diagnose */}
      <section className="bg-[#FFF7ED] py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-[#E78938] font-semibold mb-4">{t("landing.diagnose_kicker")}</div>
          <h2 className="serif text-4xl text-[#0F172A] mb-10">{t("landing.diagnose_title")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              t("landing.diag_solar"),
              t("landing.diag_battery"),
              t("landing.diag_heatpump"),
              t("landing.diag_ev"),
              t("landing.diag_inverter"),
              t("landing.diag_subsidies"),
              t("landing.diag_roi"),
              t("landing.diag_checklist"),
            ].map((it, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#FED7AA] py-5 px-3 text-sm font-medium text-[#0F172A] hover-lift">{it}</div>
            ))}
          </div>
          <img src={DOCTOR_THUMBSUP} alt="Doctor Sol" className="w-44 mx-auto mt-12" />
          <p className="text-[#0F172A] mt-4 italic text-lg max-w-xl mx-auto">{t("landing.tagline_quote")}</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
